import * as THREE from 'three';
import { Entity } from '../../entities';
import { createMatteMaterial, createGlowMaterial } from '../../visual';

export interface AstronautColors {
  readonly suit: number;
  readonly accent: number;
  readonly visor: number;
  readonly dark: number;
  readonly glove: number;
  readonly clipGlow: number;
}

const DEFAULT_COLORS: AstronautColors = {
  suit: 0xf5efe3,
  accent: 0xff8c42,
  visor: 0x0e0e18,
  dark: 0x1a1a24,
  glove: 0x14141c,
  clipGlow: 0x8ad0ff
};

export interface AstronautTickOptions {
  readonly horizontalSpeed: number;
  readonly isGrounded: boolean;
  readonly isMoving: boolean;
  readonly isRunning: boolean;
  readonly verticalSpeed: number;
}

export class Astronaut extends Entity {
  readonly velocity = new THREE.Vector3();
  readonly collisionRadius = 0.36;
  readonly standingHeight = 1.78;
  isGrounded = false;

  private readonly torso: THREE.Group;
  private readonly helmet: THREE.Group;
  private readonly leftShoulder: THREE.Group;
  private readonly rightShoulder: THREE.Group;
  private readonly leftHip: THREE.Group;
  private readonly rightHip: THREE.Group;
  private readonly leftKnee: THREE.Group;
  private readonly rightKnee: THREE.Group;
  private readonly clipLight: THREE.Mesh;

  private animTime = 0;
  private landPulse = 0;
  private wasGrounded = true;

  constructor(colors: AstronautColors = DEFAULT_COLORS) {
    super();
    this.object3D.name = 'Astronaut';

    const suitMat = createMatteMaterial({
      color: colors.suit,
      roughness: 0.78,
      flatShading: true
    });
    const accentMat = createMatteMaterial({
      color: colors.accent,
      roughness: 0.6,
      flatShading: true,
      emissive: colors.accent,
      emissiveIntensity: 0.08
    });
    const darkMat = createMatteMaterial({
      color: colors.dark,
      roughness: 0.75,
      flatShading: true
    });
    const visorMat = createMatteMaterial({
      color: colors.visor,
      roughness: 0.28,
      flatShading: true,
      emissive: colors.visor,
      emissiveIntensity: 0.15
    });
    const gloveMat = createMatteMaterial({
      color: colors.glove,
      roughness: 0.7,
      flatShading: true
    });
    const bootMat = createMatteMaterial({
      color: colors.glove,
      roughness: 0.72,
      flatShading: true
    });

    this.torso = this.buildTorso(suitMat, accentMat, darkMat);
    this.object3D.add(this.torso);

    this.helmet = new THREE.Group();
    this.helmet.position.y = 0.83;
    this.clipLight = this.buildHelmet(
      this.helmet,
      suitMat,
      accentMat,
      visorMat,
      darkMat,
      colors
    );
    this.torso.add(this.helmet);

    this.leftShoulder = this.buildArm(suitMat, accentMat, gloveMat, 1);
    this.rightShoulder = this.buildArm(suitMat, accentMat, gloveMat, -1);
    this.torso.add(this.leftShoulder, this.rightShoulder);

    const legL = this.buildLeg(suitMat, bootMat, darkMat, 1);
    this.leftHip = legL.hip;
    this.leftKnee = legL.knee;
    const legR = this.buildLeg(suitMat, bootMat, darkMat, -1);
    this.rightHip = legR.hip;
    this.rightKnee = legR.knee;
    this.object3D.add(this.leftHip, this.rightHip);
  }

  getCollisionCenter(out: THREE.Vector3): THREE.Vector3 {
    return out
      .copy(this.object3D.position)
      .add(new THREE.Vector3(0, this.standingHeight * 0.45, 0));
  }

  tick(dt: number, opts: AstronautTickOptions): void {
    this.animTime += dt;

    if (opts.isGrounded && !this.wasGrounded) {
      this.landPulse = 0.2;
    }
    this.wasGrounded = opts.isGrounded;

    if (this.landPulse > 0) {
      this.landPulse = Math.max(0, this.landPulse - dt);
      const squash = 1 - this.landPulse * 0.55;
      const spread = 1 + (1 - squash) * 0.22;
      this.torso.scale.set(spread, squash, spread);
      this.torso.position.y = 0.55 * squash;
      this.torso.rotation.set(0, 0, 0);
      this.leftHip.rotation.set(0, 0, 0);
      this.rightHip.rotation.set(0, 0, 0);
      this.leftKnee.rotation.set((1 - squash) * 1.4, 0, 0);
      this.rightKnee.rotation.set((1 - squash) * 1.4, 0, 0);
      this.leftShoulder.rotation.set(0, 0, -(1 - squash) * 0.6);
      this.rightShoulder.rotation.set(0, 0, (1 - squash) * 0.6);
      this.helmet.rotation.set(0, 0, 0);
    } else if (!opts.isGrounded) {
      this.setAirPose(opts.verticalSpeed);
    } else if (opts.isMoving && opts.horizontalSpeed > 0.2) {
      this.animateWalk(opts.horizontalSpeed, opts.isRunning);
    } else {
      this.animateIdle();
    }

    const pulse = 0.7 + Math.sin(this.animTime * 3.5) * 0.3;
    this.clipLight.scale.setScalar(pulse);
  }

  resetPose(): void {
    this.torso.scale.set(1, 1, 1);
    this.torso.position.y = 0.55;
    this.torso.rotation.set(0, 0, 0);
    this.helmet.rotation.set(0, 0, 0);
    this.leftShoulder.rotation.set(0, 0, 0);
    this.rightShoulder.rotation.set(0, 0, 0);
    this.leftHip.rotation.set(0, 0, 0);
    this.rightHip.rotation.set(0, 0, 0);
    this.leftKnee.rotation.set(0, 0, 0);
    this.rightKnee.rotation.set(0, 0, 0);
  }

  private animateIdle(): void {
    const t = this.animTime;
    const breath = Math.sin(t * 1.7) * 0.03;
    this.torso.scale.set(1, 1 + breath * 0.5, 1);
    this.torso.position.y = 0.55 + breath * 0.25;
    this.torso.rotation.set(0, 0, 0);

    const armSway = Math.sin(t * 1.4) * 0.06;
    this.leftShoulder.rotation.set(armSway, 0, 0);
    this.rightShoulder.rotation.set(-armSway, 0, 0);

    this.leftHip.rotation.set(0, 0, 0);
    this.rightHip.rotation.set(0, 0, 0);
    this.leftKnee.rotation.set(0, 0, 0);
    this.rightKnee.rotation.set(0, 0, 0);

    this.helmet.rotation.set(
      Math.sin(t * 0.8) * 0.03,
      Math.sin(t * 0.5) * 0.12,
      0
    );
  }

  private animateWalk(speed: number, running: boolean): void {
    const rate = running ? 7.8 : 5.4;
    const phase = this.animTime * rate;
    const norm = Math.min(speed / (running ? 9 : 5), 1);
    const swing = 0.6 * norm + 0.12;
    const kneeBend = 0.85 * norm + 0.15;

    const legL = Math.sin(phase);
    const legR = Math.sin(phase + Math.PI);

    this.leftHip.rotation.set(-legL * swing, 0, 0);
    this.rightHip.rotation.set(-legR * swing, 0, 0);

    this.leftKnee.rotation.set(Math.max(0, legL) * kneeBend, 0, 0);
    this.rightKnee.rotation.set(Math.max(0, legR) * kneeBend, 0, 0);

    this.leftShoulder.rotation.set(legR * swing * 0.95, 0, 0);
    this.rightShoulder.rotation.set(legL * swing * 0.95, 0, 0);

    const bob = Math.abs(Math.sin(phase)) * 0.07 * norm;
    this.torso.scale.set(1, 1, 1);
    this.torso.position.y = 0.55 + bob;

    const leanPitch = running ? 0.12 * norm : 0.05 * norm;
    this.torso.rotation.set(leanPitch, 0, 0);
    this.helmet.rotation.set(
      -leanPitch * 0.5,
      Math.sin(phase) * 0.06,
      Math.sin(phase * 2) * 0.02
    );
  }

  private setAirPose(verticalSpeed: number): void {
    const goingUp = THREE.MathUtils.clamp(verticalSpeed * 0.08, -0.35, 0.35);
    const hipPitch = -0.3 + goingUp * 0.5;
    this.leftHip.rotation.set(hipPitch, 0, 0);
    this.rightHip.rotation.set(hipPitch, 0, 0);
    this.leftKnee.rotation.set(1.0, 0, 0);
    this.rightKnee.rotation.set(1.0, 0, 0);

    this.leftShoulder.rotation.set(0.18, 0, -0.45);
    this.rightShoulder.rotation.set(0.18, 0, 0.45);

    this.torso.position.y = 0.55;
    this.torso.scale.set(1, 1, 1);
    this.torso.rotation.set(0.1, 0, 0);
    this.helmet.rotation.set(-0.08, 0, 0);
  }

  private buildTorso(
    suitMat: THREE.Material,
    accentMat: THREE.Material,
    darkMat: THREE.Material
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = 'astronaut-torso';
    group.position.y = 0.55;

    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.22, 0.26, 6, 14),
      suitMat
    );
    body.position.y = 0.2;
    body.scale.set(1.08, 1, 1);

    const yoke = new THREE.Mesh(
      new THREE.TorusGeometry(0.24, 0.065, 8, 22),
      accentMat
    );
    yoke.rotation.x = Math.PI / 2;
    yoke.position.y = 0.4;
    yoke.scale.set(1.08, 1.08, 0.8);

    const yokeTop = new THREE.Mesh(
      new THREE.BoxGeometry(0.32, 0.08, 0.22),
      accentMat
    );
    yokeTop.position.y = 0.42;

    const belt = new THREE.Mesh(
      new THREE.TorusGeometry(0.23, 0.05, 8, 22),
      darkMat
    );
    belt.rotation.x = Math.PI / 2;
    belt.position.y = 0.03;

    const buckle = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.09, 0.04),
      accentMat
    );
    buckle.position.set(0, 0.03, 0.235);

    group.add(body, yoke, yokeTop, belt, buckle);
    return group;
  }

  private buildHelmet(
    group: THREE.Group,
    suitMat: THREE.Material,
    accentMat: THREE.Material,
    visorMat: THREE.Material,
    darkMat: THREE.Material,
    colors: AstronautColors
  ): THREE.Mesh {
    group.name = 'astronaut-helmet';

    const helmet = new THREE.Mesh(
      new THREE.SphereGeometry(0.4, 26, 20),
      suitMat
    );

    const visor = new THREE.Mesh(
      new THREE.SphereGeometry(
        0.41,
        30,
        22,
        Math.PI * 0.15,
        Math.PI * 0.7,
        Math.PI * 0.22,
        Math.PI * 0.56
      ),
      visorMat
    );

    const foreheadBand = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 0.06, 0.1),
      suitMat
    );
    foreheadBand.position.set(0, 0.18, 0.32);

    const chinBand = new THREE.Mesh(
      new THREE.BoxGeometry(0.48, 0.05, 0.08),
      suitMat
    );
    chinBand.position.set(0, -0.18, 0.3);

    const clipBase = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.06, 0.08, 6),
      accentMat
    );
    clipBase.rotation.z = Math.PI / 2;
    clipBase.position.set(0.38, 0.04, -0.02);

    const clipLight = new THREE.Mesh(
      new THREE.SphereGeometry(0.028, 8, 6),
      createGlowMaterial(colors.clipGlow, 0.95)
    );
    clipLight.name = 'astronaut-clip-light';
    clipLight.position.set(0.43, 0.04, -0.02);

    const neckRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.22, 0.04, 8, 18),
      darkMat
    );
    neckRing.rotation.x = Math.PI / 2;
    neckRing.position.y = -0.36;

    group.add(helmet, visor, foreheadBand, chinBand, clipBase, clipLight, neckRing);
    return clipLight;
  }

  private buildArm(
    suitMat: THREE.Material,
    accentMat: THREE.Material,
    gloveMat: THREE.Material,
    side: 1 | -1
  ): THREE.Group {
    const shoulder = new THREE.Group();
    shoulder.name = `astronaut-shoulder-${side > 0 ? 'left' : 'right'}`;
    shoulder.position.set(side * -0.32, 0.34, 0);

    const cuff = new THREE.Mesh(
      new THREE.TorusGeometry(0.11, 0.03, 8, 16),
      accentMat
    );
    cuff.rotation.x = Math.PI / 2;

    const upperArm = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.085, 0.12, 6, 10),
      suitMat
    );
    upperArm.position.y = -0.14;

    const forearm = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.08, 0.1, 6, 10),
      suitMat
    );
    forearm.position.y = -0.32;

    const glove = new THREE.Mesh(
      new THREE.SphereGeometry(0.115, 16, 12),
      gloveMat
    );
    glove.position.y = -0.45;
    glove.scale.set(1.05, 0.88, 1);

    shoulder.add(cuff, upperArm, forearm, glove);
    return shoulder;
  }

  private buildLeg(
    suitMat: THREE.Material,
    bootMat: THREE.Material,
    darkMat: THREE.Material,
    side: 1 | -1
  ): { hip: THREE.Group; knee: THREE.Group } {
    const hip = new THREE.Group();
    hip.name = `astronaut-hip-${side > 0 ? 'left' : 'right'}`;
    hip.position.set(side * -0.12, 0.55, 0);

    const upperLeg = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.11, 0.08, 6, 10),
      suitMat
    );
    upperLeg.position.y = -0.1;

    const knee = new THREE.Group();
    knee.name = `astronaut-knee-${side > 0 ? 'left' : 'right'}`;
    knee.position.y = -0.2;

    const lowerLeg = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.1, 0.06, 6, 10),
      suitMat
    );
    lowerLeg.position.y = -0.08;

    const ankleRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.105, 0.024, 6, 14),
      darkMat
    );
    ankleRing.rotation.x = Math.PI / 2;
    ankleRing.position.y = -0.17;

    const boot = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.16, 0.3),
      bootMat
    );
    boot.position.set(0, -0.245, 0.04);

    const bootSole = new THREE.Mesh(
      new THREE.BoxGeometry(0.24, 0.05, 0.33),
      darkMat
    );
    bootSole.position.set(0, -0.325, 0.04);

    const bootToe = new THREE.Mesh(
      new THREE.SphereGeometry(0.108, 14, 10),
      bootMat
    );
    bootToe.scale.set(1, 0.55, 1);
    bootToe.position.set(0, -0.245, 0.19);

    knee.add(lowerLeg, ankleRing, boot, bootSole, bootToe);
    hip.add(upperLeg, knee);

    return { hip, knee };
  }
}
